import { create } from "zustand"

import type { ArtifactCommand, ArtifactType } from "../artifact-types"

import {
  applyUpdates,
  extractArtifacts,
  parseUpdateCommands,
} from "../streaming/artifact-stream-parser"

export type ArtifactVersion = {
  content: string
  command: ArtifactCommand
  messageId: string
  timestamp: string
}

export type ArtifactStatus = "streaming" | "complete" | "incomplete" | "error"

/** Where an artifact's embedded images are fetched from: the live chat session
 * (session-content) or a saved document's own storage (document-content). */
export type ArtifactImageSource =
  | { kind: "session"; sessionId: string }
  | { kind: "document"; documentId: string }

export type ArtifactEntry = {
  identifier: string
  type: ArtifactType
  title: string
  versions: ArtifactVersion[]
  status: ArtifactStatus
  sessionId?: string
  sessionFilenames?: string[]
  imageSource?: ArtifactImageSource
}

type IngestOptions = { streaming?: boolean; sessionId?: string; filenames?: string[] }

type ArtifactStoreState = {
  artifacts: Record<string, ArtifactEntry>
  openArtifactId: string | null
  ingestMessage: (messageId: string, rawText: string, opts?: IngestOptions) => void
  loadArtifact: (input: {
    identifier: string
    type: ArtifactType
    title: string
    content: string
    sessionId?: string
    filenames?: string[]
    imageSource?: ArtifactImageSource
  }) => void
  /**
   * Drops `${sessionId}:${index}` entries whose index is greater than `maxIndex`.
   * Called from the last rendered message so that when a chat turn is deleted or
   * reordered (shrinking the message list), orphaned trailing keys don't keep
   * surfacing artifacts from turns that are no longer in the thread.
   */
  pruneMessagesBeyond: (sessionId: string, maxIndex: number) => void
  /**
   * Records that a session artifact has been saved/shared as the document
   * `documentId`, so `rebuild()` promotes its image source to `document` and the
   * Share action reuses that document instead of creating a duplicate.
   *
   * `sessionId` MUST be the artifact's own session (`entry.sessionId`), not the
   * module-level `activeSessionId`: callers await the create request first, and a
   * chat switch mid-flight would otherwise write under the wrong session's key.
   */
  markArtifactShared: (
    identifier: string,
    documentId: string,
    sessionId: string | undefined
  ) => void
  setOpenArtifactId: (id: string | null) => void
  reset: () => void
}

// Raw-text bookkeeping lives outside Zustand state because it is derived input
// used for deterministic rebuild, not render state.  It is cleared by reset().
// If this store were ever namespaced (multiple instances), this Map would need
// to move inside the store factory.
const messageTexts = new Map<
  string,
  {
    text: string
    streaming: boolean
    firstIngestedAt: string
    sessionId?: string
    filenames?: string[]
  }
>()

// Saved artifacts opened from the library (loadArtifact) live here, separate
// from the streaming-derived `messageTexts`.  rebuild() merges these in so that
// a loaded document is not clobbered when ingestMessage replaces the artifacts
// map.  Streamed artifacts take precedence on the (impossible) id collision.
// Cleared by reset().
const loadedArtifacts = new Map<string, ArtifactEntry>()

// Per-message parse cache. extractArtifacts is pure over (text, messageId), so a
// message whose accumulated text hasn't changed since the last rebuild reuses
// its parsed blocks. Without this, rebuild() re-parses EVERY message's full text
// on every stream chunk — O(chunks × total session text). Cleared with
// messageTexts (session switch / reset).
const parseCache = new Map<
  string,
  { text: string; blocks: ReturnType<typeof extractArtifacts>["blocks"] }
>()

// Session artifacts that have been saved/shared as a document map their
// `${sessionId}::${identifier}` → the created documentId. rebuild() promotes
// these to a `document` image source so (a) their images resolve from
// document-scoped storage and (b) the Share action reuses the same document
// instead of creating a fresh, participant-less duplicate on every click.
//
// The key is session-scoped because artifact identifiers (e.g. "rapport") are
// reused across chats: keying by identifier alone would let a share in one chat
// bleed into a same-named artifact in another chat (wrong document + wrong image
// storage). Cleared by reset().
const sharedDocumentIds = new Map<string, string>()
const sharedDocKey = (sessionId: string | undefined, identifier: string): string =>
  `${sessionId ?? ""}::${identifier}`

// Tracks the active chat session so that messageTexts is cleared when the user
// switches sessions.  An identifier like "rapport" is reused across sessions
// by design; without session scoping the Map would accumulate stale entries and
// rebuild() would merge versions across unrelated sessions.
let activeSessionId: string | undefined

type BlockProcessParams = {
  entry: ArtifactEntry
  block: ReturnType<typeof extractArtifacts>["blocks"][number]
  messageId: string
  streaming: boolean
  timestamp: string
}

function processBlock({
  entry,
  block,
  messageId,
  streaming,
  timestamp,
}: BlockProcessParams): ArtifactEntry {
  // An `update` patches content only — it must NOT redefine type/title. The
  // parser defaults an omitted type→"markdown" and title→identifier, so taking
  // them from an update block would clobber the artifact's real type/title.
  // create/rewrite legitimately set them.
  const isUpdate = block.command === "update"
  const title = isUpdate ? entry.title || block.title : block.title || entry.title
  const type = isUpdate ? entry.type : block.type

  let status: ArtifactStatus
  if (!block.complete) {
    status = streaming ? "streaming" : "incomplete"
  } else {
    status = "complete"
  }

  let versions = entry.versions

  // Handle update vs create/rewrite commands
  if (block.command === "update" && block.complete) {
    const base = entry.versions.at(-1)?.content ?? ""
    const pairs = parseUpdateCommands(block.rawContent)
    if (pairs.length === 0 && block.rawContent.trim() !== "") {
      // A non-empty update body that yields no valid <old_str>/<new_str> pairs is
      // malformed — surface it as an error rather than pushing a duplicate,
      // unchanged version and reporting success.
      status = "error"
    } else {
      const result = applyUpdates(base, pairs)
      if (result.ok) {
        versions = [
          ...entry.versions,
          {
            content: result.content,
            command: "update",
            messageId,
            timestamp,
          },
        ]
      } else {
        status = "error"
      }
    }
  } else if (block.command !== "update") {
    // create or rewrite
    versions = [
      ...entry.versions,
      {
        content: block.rawContent,
        command: block.command,
        messageId,
        timestamp,
      },
    ]
  }

  return { ...entry, title, type, status, versions }
}

function rebuild(): Record<string, ArtifactEntry> {
  // Start from loaded (saved) artifacts so they survive every rebuild.  Entries
  // derived below from streamed messages overwrite these on an id collision.
  const artifacts: Record<string, ArtifactEntry> = Object.fromEntries(loadedArtifacts)
  // Iterate by the chronological index embedded in `${sessionId}:${index}` keys
  // so version history is derived from event content, not Map insertion order.
  const ordered = [...messageTexts.entries()].sort(([a], [b]) => {
    const ia = Number(a.slice(a.lastIndexOf(":") + 1))
    const ib = Number(b.slice(b.lastIndexOf(":") + 1))
    return Number.isNaN(ia) || Number.isNaN(ib) ? 0 : ia - ib
  })
  for (const [messageId, { text, streaming, firstIngestedAt, sessionId, filenames }] of ordered) {
    // Reuse cached parse when this message's accumulated text is unchanged;
    // messageId seeds the missing-identifier fallback so two messages that both
    // omit `identifier` don't collide on `artifact-0`.
    let cached = parseCache.get(messageId)
    if (!cached || cached.text !== text) {
      cached = { text, blocks: extractArtifacts(text, messageId).blocks }
      parseCache.set(messageId, cached)
    }
    for (const block of cached.blocks) {
      const entry: ArtifactEntry = artifacts[block.identifier] ?? {
        identifier: block.identifier,
        type: block.type,
        title: block.title,
        versions: [],
        status: "complete",
      }

      const updated = processBlock({
        entry,
        block,
        messageId,
        streaming,
        timestamp: firstIngestedAt,
      })
      updated.sessionId = sessionId
      if (sessionId) updated.imageSource = { kind: "session", sessionId }
      // Once a session artifact has been saved/shared, its images live in
      // document storage and its Share action must reuse that document — promote
      // it to a `document` source, overriding the session source derived above.
      // Scoped to this message's session so a same-id artifact in another chat
      // is never promoted to the wrong document.
      const sharedDocId = sharedDocumentIds.get(sharedDocKey(sessionId, block.identifier))
      if (sharedDocId) updated.imageSource = { kind: "document", documentId: sharedDocId }
      // Accumulate image filenames across turns (union) instead of overwriting
      // with the latest message's list. An update turn usually carries no new
      // image (filenames=[]); overwriting would drop the create turn's images
      // and break sandbox:/mnt/data/… resolution for the artifact. Session-
      // scoped — messageTexts clears on session switch — so there is no leak.
      updated.sessionFilenames = Array.from(
        new Set([...(updated.sessionFilenames ?? []), ...(filenames ?? [])])
      )
      artifacts[block.identifier] = updated
    }
  }
  return artifacts
}

const defaultArtifactStoreState: Omit<
  ArtifactStoreState,
  | "ingestMessage"
  | "loadArtifact"
  | "pruneMessagesBeyond"
  | "markArtifactShared"
  | "setOpenArtifactId"
  | "reset"
> = {
  artifacts: {},
  openArtifactId: null,
}

export const useArtifactStore = create<ArtifactStoreState>((set) => ({
  ...defaultArtifactStoreState,
  ingestMessage: (messageId, rawText, opts) => {
    const sessionId = opts?.sessionId
    const streaming = opts?.streaming ?? false

    // Guard: non-session-scoped caller must not pollute the tracked session's map.
    // If a session is being tracked but this ingest lacks a sessionId, ignore it.
    if (sessionId === undefined && activeSessionId !== undefined) {
      return
    }

    // Session switch: when a new sessionId arrives, discard all in-memory data
    // from the previous session and start fresh.  This prevents stale entries
    // from an earlier chat from being merged into the current one via rebuild().
    if (sessionId !== undefined && sessionId !== activeSessionId) {
      messageTexts.clear()
      parseCache.clear()
      activeSessionId = sessionId
    }

    const filenames = opts?.filenames
    const previous = messageTexts.get(messageId)
    if (previous && previous.text === rawText && previous.streaming === streaming) return
    const firstIngestedAt = previous?.firstIngestedAt ?? new Date().toISOString()
    messageTexts.set(messageId, { text: rawText, streaming, firstIngestedAt, sessionId, filenames })
    set({ artifacts: rebuild() })
  },
  // Inserts a fully-formed, saved artifact into `loadedArtifacts` (a map kept
  // separate from `messageTexts`) and reflects it in state via the same merge
  // rebuild() uses.  Because loaded artifacts are merged on every rebuild(), a
  // persisted document survives a later ingestMessage instead of being dropped
  // by the session-streaming derivation.  Used when opening a saved
  // non-markdown document from the library.
  loadArtifact: ({ identifier, type, title, content, sessionId, filenames, imageSource }) => {
    loadedArtifacts.set(identifier, {
      identifier,
      type,
      title,
      status: "complete",
      versions: [{ content, command: "create", messageId: identifier, timestamp: "" }],
      sessionId,
      sessionFilenames: filenames ?? [],
      imageSource,
    })
    set({ artifacts: rebuild() })
  },
  pruneMessagesBeyond: (sessionId, maxIndex) => {
    const prefix = `${sessionId}:`
    let removed = false
    for (const key of messageTexts.keys()) {
      if (!key.startsWith(prefix)) continue
      const index = Number(key.slice(prefix.length))
      if (Number.isInteger(index) && index > maxIndex) {
        messageTexts.delete(key)
        removed = true
      }
    }
    if (removed) set({ artifacts: rebuild() })
  },
  markArtifactShared: (identifier, documentId, sessionId) => {
    // Scope to the artifact's OWN session (passed by the caller via
    // entry.sessionId), matching how rebuild() derives its lookup key. Using the
    // module-level activeSessionId here would be racy: the share handler awaits
    // the create request, and a chat switch in that window would flip
    // activeSessionId and file this under the wrong session.
    sharedDocumentIds.set(sharedDocKey(sessionId, identifier), documentId)
    set({ artifacts: rebuild() })
  },
  setOpenArtifactId: (id) => set({ openArtifactId: id }),
  reset: () => {
    messageTexts.clear()
    parseCache.clear()
    loadedArtifacts.clear()
    sharedDocumentIds.clear()
    activeSessionId = undefined
    set(defaultArtifactStoreState)
  },
}))
